import {
  useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { en } from './i18n/en';
import { I18nContext, type I18nContextValue } from './i18nContext';
import {
  currentLanguage, setActiveLanguage, type Language,
} from './activeLanguage';

/* =============================================================================
 * Translation
 * -----------------------------------------------------------------------------
 * Deliberately small. A full i18n library would add 40 kB to a bundle that runs
 * on 2 GB phones, to solve a problem this app has in only one place.
 *
 * The scope is chosen, not accidental: the *employee* screens are translated,
 * because those are read by workers whose first language is not English. The
 * admin screens stay in English — they are used by office staff, and a
 * half-translated payroll interface is more dangerous than an untranslated one.
 *
 * Adding a language is one object below. Adding Urdu or Hindi later needs no
 * code change at all.
 * ========================================================================== */

/* Declared in ./activeLanguage and re-exported here, which is where the whole
   application imports it from. The holder moved out so that a formatter — and
   through it the export dictionary, and through that an Edge Function — can read
   the language without importing React; see the note in that file. */
export type { Language };
export { currentLanguage };

/* =============================================================================
 * Two dictionaries, and only one of them is in the bundle
 * -----------------------------------------------------------------------------
 * Both languages used to live in this file, in one object, imported eagerly.
 * That put 105 kB gzipped into the entry chunk — a third of everything a phone
 * downloaded before the login screen painted — and every reader got both halves
 * to use one of them.
 *
 * It was worse than an even split suggests. The admin screens are deliberately
 * English-only, for a stated reason: a half-translated payroll interface is
 * more dangerous than an untranslated one. So an office session downloaded 53 kB
 * of Arabic it could not have displayed if it wanted to.
 *
 * English stays. It is the fallback for a missing entry in any language, so
 * there is no session that does not need it. Arabic is fetched, once, by the
 * sessions that are in Arabic.
 *
 * -----------------------------------------------------------------------------
 * WHY THIS IS NOT ASYNC AT THE POINT OF USE
 * -----------------------------------------------------------------------------
 * `translate` is called during render, from components and from plain
 * formatters that are not components at all. It cannot await anything.
 *
 * So the load happens at two moments, both before anything reads a key:
 *
 *   at boot     main.tsx awaits loadLanguage() for the stored language before
 *               it renders at all, so an Arabic device paints Arabic on its
 *               first frame rather than a flash of English running the wrong
 *               way — which is what the stored-language cache exists to prevent
 *               and would have been undone by fetching after the first paint
 *
 *   on change   setLanguage fetches before it commits the new language, so the
 *               interface changes once, completely, instead of flipping to
 *               English and filling in
 *
 * A key asked for before either has finished still answers — in English, and
 * recorded as missing. That is the same behaviour as an untranslated entry,
 * which is a state this application already handles rather than a new one.
 * ========================================================================== */

export type TranslationKey = keyof typeof en;

/** Every language but English is partial: an unwritten entry falls back. */
type Dictionary = Partial<Record<TranslationKey, string>>;

const DICTIONARY: Record<Language, Dictionary> = { en, ar: {} };

/* Module state rather than React state on purpose. `translate` is reachable
   from outside the tree — formatMoney, the export documents, a few plain
   helpers — and none of those can read a context. */
let loaded: Partial<Record<Language, boolean>> = { en: true };

/** Whether a language can currently be rendered without falling back. */
export function isLanguageLoaded(language: Language): boolean {
  return loaded[language] === true;
}

/**
 * Fetches a language's dictionary, once.
 *
 * Safe to call repeatedly and from several places at the same time: the promise
 * is held, so two components mounting in one tick share a single request rather
 * than starting two.
 */
const loading = new Map<Language, Promise<void>>();

export function loadLanguage(language: Language): Promise<void> {
  if (loaded[language]) return Promise.resolve();

  const existing = loading.get(language);
  if (existing) return existing;

  const pending = import('./i18n/ar')
    .then((module) => {
      DICTIONARY.ar = module.ar;
      loaded = { ...loaded, ar: true };
    })
    .catch((error: unknown) => {
      /* Not remembered as loaded, so a language switch that failed on a dropped
         connection can be retried by simply asking again. The interface stays
         in English in the meantime, which is readable — a raw key is not. */
      loading.delete(language);
      if (import.meta.env.DEV) {
        console.warn(`[i18n] could not load ${language}`, error);
      }
    });

  loading.set(language, pending);
  return pending;
}

/** Test seam: forget what has been loaded. */
export function resetLoadedLanguages(): void {
  loaded = { en: true };
  loading.clear();
  DICTIONARY.ar = {};
}

/** Narrows a value read from the database or from storage. */
export function isLanguage(value: unknown): value is Language {
  return value === 'en' || value === 'ar';
}

export const LANGUAGES: Array<{ code: Language; label: string; nativeLabel: string }> = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
];

const RTL_LANGUAGES: readonly Language[] = ['ar'];

/* Remembered on the device as well as against the profile.
   -----------------------------------------------------------------------------
   The profile is the record — it is what follows somebody to a different
   handset. This is so the app opens in the right language and the right
   direction on the *first* paint, before any query has returned. Without it
   every reload shows half a second of English running the wrong way, which on a
   cheap phone is long enough to look broken. */
const STORAGE_KEY = 'noorone.lang';

/* -----------------------------------------------------------------------------
 * Keys with no translation
 * -----------------------------------------------------------------------------
 * `t` falls back to English, because a screen with an English word on it is
 * usable and a screen with a raw key is not.
 *
 * The trouble is that the fallback is comfortable. A half-translated screen
 * looks finished to whoever built it and reads as broken to whoever uses it,
 * and nothing announces the difference — which is precisely how the export
 * dictionaries drifted until a test was pointed at them.
 *
 * So each miss is recorded once. In development it is warned about; in a test
 * the list can be asserted on; in production it costs one Set insertion and
 * says nothing, because a user does not need telling the developers are behind.
 * -------------------------------------------------------------------------- */
const missing = new Set<string>();

export function missingTranslations(): string[] {
  return [...missing].sort();
}

export function clearMissingTranslations(): void {
  missing.clear();
}

/**
 * A key in a language, falling back to English and recording the gap.
 *
 * Exported so the behaviour can be tested as what it is — a lookup with a
 * fallback — rather than through a provider that would need a DOM and a
 * renderer to prove something neither is involved in.
 */
export function translate(
  key: TranslationKey,
  language: Language,
  vars?: Vars,
): string {
  const found = DICTIONARY[language][key];
  if (found) return fill(found, vars);

  if (language !== 'en' && !missing.has(key)) {
    missing.add(key);
    if (import.meta.env.DEV) {
      console.warn(`[i18n] no ${language} for ${key}`);
    }
  }

  return fill(DICTIONARY.en[key] ?? key, vars);
}

/* -----------------------------------------------------------------------------
 * Sentences with something in them
 * -----------------------------------------------------------------------------
 * A sentence that carries a name or a number cannot be translated by cutting it
 * into pieces and joining them back in English order:
 *
 *     `${count} people have not come back from leave`
 *
 * Arabic does not put the subject, the verb and the number where English does,
 * so a translation assembled from three fragments in the English sequence reads
 * as three fragments. The whole sentence has to be one entry, with a slot in it
 * that each language may put wherever that language puts it.
 * -------------------------------------------------------------------------- */

export type Vars = Record<string, string | number>;

function fill(text: string, vars?: Vars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = vars[name];
    return value === undefined ? whole : String(value);
  });
}

/* -----------------------------------------------------------------------------
 * Counting in Arabic
 * -----------------------------------------------------------------------------
 * English has two shapes — one day, two days — and a codebase written in English
 * learns to think that a plural is an `s` on the end. Arabic has six, and they
 * are not decoration: one day is يوم, two days is يومان with its own dual form,
 * three to ten take a plural noun, eleven and up take a singular accusative, and
 * a hundred takes yet another. Writing `${n} أيام` gets four of the six wrong,
 * which is most of the numbers anybody will actually see.
 *
 * So a counted sentence is stored as several entries, suffixed with the CLDR
 * category, and `Intl.PluralRules` — which already knows all six for every
 * language — picks between them. English only ever declares `.one` and
 * `.other`; the lookup falls back through `.other`, so an Arabic entry that has
 * not been written yet degrades to a readable sentence rather than a raw key.
 * -------------------------------------------------------------------------- */

/** The stem of a counted entry: `x.one` and `x.other` in the dictionary give `x`. */
type BaseOf<K> = K extends `${infer Base}.other` ? Base : never;
export type PluralKey = BaseOf<TranslationKey>;

const PLURAL_RULES: Partial<Record<Language, Intl.PluralRules>> = {};

function rulesFor(language: Language): Intl.PluralRules {
  return (PLURAL_RULES[language] ??= new Intl.PluralRules(
    language === 'ar' ? 'ar' : 'en',
  ));
}

/**
 * A counted sentence in a language.
 *
 * `count` is passed through as a variable as well as choosing the wording, so a
 * dictionary entry can say `{count} people` without the caller repeating it.
 */
export function plural(
  base: PluralKey,
  count: number,
  language: Language,
  vars?: Vars,
): string {
  const category = rulesFor(language).select(count);
  const withCount = { count: formatCount(count, language), ...vars };

  const key = `${base}.${category}` as TranslationKey;
  if (DICTIONARY[language][key]) return translate(key, language, withCount);

  return translate(`${base}.other` as TranslationKey, language, withCount);
}

/* Latin digits in both languages, deliberately. Payroll figures on this screen
   are read against bank statements and MOHRE filings, which are Latin. */
function formatCount(count: number, language: Language): string {
  return new Intl.NumberFormat(
    language === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE',
  ).format(count);
}

/* The context and its shape live in ./i18nContext, which exists to hold nothing
   else. A context is matched by object identity, and this module is re-evaluated
   in development every time a dictionary changes — which made a new context
   object while the mounted provider kept handing out the old one, and every
   screen below it threw "useI18n must be used inside <I18nProvider>" with the
   provider present and correct. See that file. */

function readStored(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'ar') return stored;
  } catch {
    /* storage unavailable */
  }
  return 'en';
}

/* What the reader chose last time, read once as this module loads.
   -----------------------------------------------------------------------------
   The holder itself lives in ./activeLanguage — it is read by money, durations
   and dates, none of which are components, and one of them is reached by an
   Edge Function. What belongs here is the browser half: the stored choice, and
   keeping the holder in step with the provider.

   Set at module scope as well as on change, because the first render formats
   money before any effect has run. It was once declared and never assigned, and
   everything that reads it rather than a hook stayed English on a fully Arabic
   screen — a hundred apparent omissions from one missing line. */
setActiveLanguage(readStored());

export function I18nProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(readStored);
  const dir = RTL_LANGUAGES.includes(language) ? 'rtl' : 'ltr';

  /* During render, not in an effect. A formatter called while this render is
     still going — a table cell, a card, anything below this provider — reads
     the module copy, and an effect would set it one paint too late: the first
     frame after a language change would carry the old currency. */
  setActiveLanguage(language);

  useEffect(() => {
    // `dir` on the root is what flips the whole layout — Tailwind's logical
    // properties, flex direction and text alignment all follow it, so no
    // component needs to know which way round it is.
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
  }, [dir, language]);

  /* Written before the fetch, switched after it.
     -------------------------------------------------------------------------
     The two halves are deliberately in this order.

     Storage first, because that is the record of what somebody chose. If the
     fetch fails on a dropped connection they still asked for Arabic, and the
     next launch — which awaits this same load before it paints — should honour
     it rather than quietly reverting them to English.

     The language itself last, because committing it before the dictionary
     arrives would repaint the whole interface in English with the layout
     flipped to right-to-left, and then fill the words in a moment later. One
     change, complete, is worth the wait; the file is small and it is fetched
     once for the life of the install. */
  const setLanguage = useCallback((next: Language) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }

    if (isLanguageLoaded(next)) {
      setLanguageState(next);
      return;
    }

    void loadLanguage(next).then(() => setLanguageState(next));
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      dir,
      setLanguage,
      t: (key, vars) => translate(key, language, vars),
      plural: (base, count, vars) => plural(base, count, language, vars),
    }),
    [language, dir, setLanguage],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/** Shorthand for the common case. */
export function useT(): (key: TranslationKey, vars?: Vars) => string {
  return useI18n().t;
}

/** Shorthand for the counted case. */
export function usePlural(): (base: PluralKey, count: number, vars?: Vars) => string {
  return useI18n().plural;
}
